import { getBreakpoint } from '../utils/_breakpoints';
import { getFocusableElements, setTabindex } from '../utils/_domFunctions';

class mAccordion extends HTMLDivElement {
    constructor() {
        super();
        this.buttonEl = this.querySelector('button[aria-expanded]');
        this.controlsId = this.buttonEl ? this.buttonEl.getAttribute('aria-controls') : null;
        this.panelEl = this.buttonEl ? document.getElementById(this.controlsId) : null;
        this.isPanelOpen = this.buttonEl ? this.buttonEl.getAttribute('aria-expanded') === 'true' : false;
        this.inTransition = false;
        this.isMobileOnly = false;
        this.desktopMql = null;
        this.watchElements = this.querySelectorAll('[data-accordion-watch]'); // Elements to watch to monitor the panel refresh
        this.togglePanel(this.isPanelOpen);

        // method binding
        this.togglePanel = this.togglePanel.bind(this);
        this.onPanelTransitionStart = this.onPanelTransitionStart.bind(this);
        this.onPanelTransitionEnd = this.onPanelTransitionEnd.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);
        this.onScreenChange = this.onScreenChange.bind(this);
    }

    connectedCallback() {
        // Error checks
        if (!this.buttonEl) {
            throw new Error(`${this.constructor.name}: No button with aria-expanded attribute found inside component`);
        }
        if (!this.panelEl || !this.contains(this.panelEl)) {
            throw new Error(`${this.constructor.name}: No panel block with id "${this.controlsId}" found inside component`);
        }

        this.buttonEl.addEventListener('click', this.togglePanel);
        this.panelEl.addEventListener('transitionstart', this.onPanelTransitionStart);
        this.panelEl.addEventListener('transitionend', this.onPanelTransitionEnd);

        // Setup if accordion is mobile only
        this.isMobileOnly = ('mobileOnly' in this.dataset);
        if (this.isMobileOnly) {
            const breakpoint = getBreakpoint('md').minWidth;
            this.desktopMql = window.matchMedia(`screen and (min-width: ${breakpoint})`);
            this.desktopMql.addEventListener('change', this.onScreenChange);
            this.onScreenChange(this.desktopMql);
        }
        this.watchElementsForRefresh();
        this.updateVisibility();
    }

    /**
     * Watch elements to know if the height of the panel has changed to refresh the max-height, the attributes watched
     * are defined by the dataset attribute : data-accordion-watch, separated by , (ex: data-accordion-watch="hidden,value")
     */
    watchElementsForRefresh() {
        this.watchElements.forEach((el) => {
            const attributesToWatch = el.dataset.accordionWatch.split(',');
            const watchConfig = {
                attributes: true,
                characterData: true,
                attributeOldValue: true,
                attributeFilter: attributesToWatch,
            };
            const watchCallback = (mutations) => {
                for (const mutation of mutations) {
                    const attributeValue = mutation.target[mutation.attributeName] && mutation.target[mutation.attributeName].toString();
                    const oldValue = mutation.oldValue && mutation.oldValue.toString();
                    // Check if the text has changed of if the value of the watched attributes has changed
                    if ((mutation.type === 'attributes' && attributeValue !== oldValue) || mutation.type === 'characterData') {
                        if (this.isPanelOpen) {
                            this.togglePanel(true);
                        }
                    }
                }
            };
            const observer = new MutationObserver(watchCallback);
            observer.observe(el, watchConfig);
        });
    }

    /**
     * Change panel element visibility state
     * @param {Event|boolean} [state] PointerclickEvent or forced visibility state boolean
     */
    togglePanel(state) {
        if (state instanceof Event) { // triggered by click listener
            this.isPanelOpen = !this.isPanelOpen;
        } else { // triggered by onScreenChange
            this.isPanelOpen = state;
        }

        // Trigger the transition
        if (this.isPanelOpen) {
            this.panelEl.style.maxHeight = `${this.panelEl.scrollHeight}px`;
        } else {
            this.panelEl.style.maxHeight = `${this.panelEl.scrollHeight}px`;

            // Reading the offsetHeight property forces a reflow,
            // which allows the transition to occur when we set max-height to 0.
            const reflow = this.panelEl.offsetHeight;

            this.panelEl.style.maxHeight = '0';
        }
    }

    rotateArrow() {
        if (this.isPanelOpen) {
            this.classList.add('open');
        } else {
            this.classList.remove('open');
        }
    }

    /**
     * Update aria attributes and focus state according to component state
     */
    updateVisibility() {
        this.buttonEl.setAttribute('aria-expanded', this.isPanelOpen);
        this.panelEl.setAttribute('aria-hidden', !this.isPanelOpen); // triggers the css side max-height when hidden
        setTabindex(getFocusableElements(this.panelEl), this.isPanelOpen ? 0 : -1);
        this.rotateArrow();
    }

    /**
     * "transitionstart" callback for the panel element
     * @param {TransitionEvent} event Transition start event
     */
    onPanelTransitionStart(event) {
        if (event.target === this.panelEl && event.propertyName === 'max-height') {
            this.inTransition = true;
            this.updateVisibility();
        }
    }

    /**
     * "transitionend" callback for the panel element
     * @param {TransitionEvent} event Transition end event
     */
    onPanelTransitionEnd(event) {
        if (event.target === this.panelEl && event.propertyName === 'max-height') {
            this.inTransition = false;

            if (this.isPanelOpen) {
                this.panelEl.style.maxHeight = 'none';
            }

            this.updateVisibility();
        }
    }

    onScreenChange(mql) {
        if (!this.isMobileOnly) return;
        const desktop = mql.matches;
        this.togglePanel(desktop);
    }
}

customElements.define('m-accordion', mAccordion, {
    extends: 'div',
});
